/**
 * The one place that talks to the API.
 *
 * Two kinds of caller, two kinds of credential: the workstation sends a
 * Cognito bearer token, the phone sends its job-scoped capture token. They are
 * never mixed -- the phone has no login and the workstation never holds a
 * capture token for a job it is not looking at.
 */
import { config } from './config';
import type { CaptureTiming } from './camera';
import type {
  CalibrationSession, CaptureCamera, Job, LensStatus, PrinterCalibration, PrinterDetail, PrinterSummary,
  Sheet, SurveyStatus,
} from './model';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

type Credential =
  | { kind: 'bearer'; token: string }
  | { kind: 'capture'; token: string }
  | { kind: 'none' };

async function request<T>(
  path: string,
  credential: Credential,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('content-type', 'application/json');
  if (credential.kind === 'bearer') {
    headers.set('authorization', `Bearer ${credential.token}`);
  } else if (credential.kind === 'capture') {
    headers.set('x-capture-token', credential.token);
  }

  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
  const text = await response.text();
  const body = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new ApiError(response.status, body.error ?? response.statusText);
  }
  return body as T;
}

/**
 * Upload straight to S3 with a presigned URL.
 *
 * Deliberately not through the API: API Gateway caps a body at 10 MB and a
 * 12 MP photograph goes past that without trying. The signed URL carries its
 * own authorisation, so no credential is attached here.
 */
export async function putToSignedUrl(
  url: string, file: Blob, contentType: string,
): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': contentType },
  });
  if (!response.ok) {
    throw new ApiError(response.status,
                       `the upload was rejected (${response.status})`);
  }
}

// -- workstation ------------------------------------------------------------

export function workstation(token: string) {
  const auth: Credential = { kind: 'bearer', token };
  const get = <T>(path: string) => request<T>(path, auth);
  const post = <T>(path: string, body?: unknown) =>
    request<T>(path, auth, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  const put = <T>(path: string, body: unknown) =>
    request<T>(path, auth, { method: 'PUT', body: JSON.stringify(body) });

  return {
    listJobs: () => get<{ jobs: Job[] }>('/jobs'),
    getJob: (jobId: string) => get<Job>(`/jobs/${jobId}`),
    createJob: (input: {
      name: string; mesh: number; printerId: string; filename: string;
    }) => post<Job>('/jobs', input),
    renameJob: (jobId: string, name: string) =>
      put<Job>(`/jobs/${jobId}`, { name }),
    archiveJob: (jobId: string, archived: boolean) =>
      post<Job>(`/jobs/${jobId}/archive`, { archived }),
    artworkUpload: (jobId: string) =>
      post<{ url: string; key: string }>(`/jobs/${jobId}/artwork-upload`),
    thumbnailUpload: (jobId: string) =>
      post<{ url: string; key: string }>(`/jobs/${jobId}/thumbnail-upload`),
    captureToken: (jobId: string) =>
      post<{ url: string; expiresAt: string }>(`/jobs/${jobId}/capture-token`),
    createSheet: (jobId: string) => post<Sheet>(`/jobs/${jobId}/sheets`),
    listSheets: (jobId: string) =>
      get<{ sheets: Sheet[] }>(`/jobs/${jobId}/sheets`),
    getSheet: (jobId: string, sheetId: string) =>
      get<{ sheet: Sheet; overlays: Record<string, string>;
            report: string | null }>(
        `/jobs/${jobId}/sheets/${sheetId}`),
    /**
     * The printer camera's photograph; the phone has its own route. `capture`
     * is how long the shot took, which only the browser knows; the API logs it.
     */
    photoUpload: (jobId: string, sheetId: string, capture?: CaptureTiming) =>
      post<{ url: string; key: string; updatedAt?: string }>(
        `/jobs/${jobId}/sheets/${sheetId}/photo-upload`,
        capture ? { capture } : undefined),
    approve: (jobId: string, sheetId: string, reviewedCrops: string[]) =>
      post<{ state: string }>(
        `/jobs/${jobId}/sheets/${sheetId}/approve`, { reviewedCrops }),
    reject: (jobId: string, sheetId: string, reason: string) =>
      post<{ state: string }>(
        `/jobs/${jobId}/sheets/${sheetId}/reject`, { reason }),
    pdf: (jobId: string, sheetId: string) =>
      get<{ url: string; filename: string }>(
        `/jobs/${jobId}/sheets/${sheetId}/pdf`),
    listPrinters: () => get<{ printers: PrinterSummary[] }>('/printers'),
    createPrinter: (input: { printerId: string; name: string }) =>
      post<{ printerId: string; name: string }>('/printers', input),
    getPrinter: (printerId: string) =>
      get<PrinterDetail>(`/printers/${printerId}`),
    putPrinter: (printerId: string,
                 input: {
                   name?: string; calibration?: PrinterCalibration;
                   camera?: CaptureCamera | null;
                 }) =>
      put<unknown>(`/printers/${printerId}`, input),
    calibrationUpload: (printerId: string) =>
      post<CalibrationSession>(`/printers/${printerId}/calibration-upload`),
    lensUpload: (printerId: string) =>
      post<CalibrationSession>(`/printers/${printerId}/lens-upload`),
    printerCaptureToken: (printerId: string) =>
      post<{ url: string; expiresAt: string }>(
        `/printers/${printerId}/capture-token`),
  };
}

// -- phone ------------------------------------------------------------------

export function capture(jobId: string, token: string) {
  const auth: Credential = { kind: 'capture', token };
  return {
    context: () => request<{
      job: { jobId: string; name: string; mesh: number };
      sheets: Pick<Sheet, 'sheetId' | 'state' | 'refusal'>[];
    }>(`/capture/${jobId}`, auth),
    photoUpload: (sheetId: string) =>
      request<{ url: string; key: string }>(
        `/capture/${jobId}/photo-upload`, auth,
        { method: 'POST', body: JSON.stringify({ sheetId }) }),
    status: (sheetId: string) =>
      request<Pick<Sheet, 'sheetId' | 'state' | 'refusal' | 'updatedAt'>>(
        `/capture/${jobId}/sheets/${sheetId}`, auth),
  };
}

/** The phone arriving by a printer's QR, to photograph its calibration print (F11). */
export function printerCapture(printerId: string, token: string) {
  const auth: Credential = { kind: 'capture', token };
  return {
    context: () => request<SurveyStatus & LensStatus & {
      printer: { printerId: string; name: string };
    }>(`/capture/printers/${printerId}`, auth),
    lensUpload: () =>
      request<CalibrationSession>(
        `/capture/printers/${printerId}/lens-upload`, auth,
        { method: 'POST' }),
    calibrationUpload: () =>
      request<CalibrationSession>(
        `/capture/printers/${printerId}/calibration-upload`, auth,
        { method: 'POST' }),
  };
}
